import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { usePanelTokens } from "../../../utils/panelTokens";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { listMotors } from "./api";
import SubmitPartDialog from "../propulsionCalc/SubmitPartDialog";
import { comboPath, motorPath, FREE_COMBO_COUNT } from "./catalog";
import { calculatorPath } from "../propulsionCalc/paths";
import CollectionToolbar, { FilterChips, FilterSelect } from "../../Templates/CollectionToolbar";
import CollectionTable from "../../Templates/CollectionTable";

const PAGE = 60;

/**
 * Public index of the motor catalog.
 *
 * Most entries are specs only — a motor is listed as soon as we hold its
 * published figures, long before it reaches the thrust stand. Entries that DO
 * have a bench comparison link through to it, and only those count against the
 * free tier (FREE_COMBO_COUNT): the measured data is what an account buys,
 * while browsing the catalog stays open so the page is findable and indexable.
 */
/** The card's stats, as columns — same figures, denser reading. */
const MOTOR_COLUMNS = [
  { key: "brand", label: "Brand" },
  { key: "name", label: "Motor" },
  { key: "kv", label: "KV", align: "right", render: (m) => (m.kv ? Math.round(m.kv) : "—") },
  {
    key: "weightG",
    label: "Weight",
    align: "right",
    render: (m) => (m.weightG ? `${Math.round(m.weightG)} g` : "—"),
  },
  {
    key: "maxCurrentA",
    label: "Max A",
    align: "right",
    render: (m) => (m.maxCurrentA ? Math.round(m.maxCurrentA) : "—"),
  },
  { key: "comboSlug", label: "Bench data", render: (m) => (m.comboSlug ? "Tested" : "Specs only") },
];

const MotorDataIndex = () => {
  const t = usePanelTokens();
  const navigate = useNavigate();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("all");
  const [tested, setTested] = useState("all");
  const [view, setView] = useState("cards");
  const [shown, setShown] = useState(PAGE);
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    listMotors().then((result) => alive && setData(result));
    return () => {
      alive = false;
    };
  }, []);

  const motors = data?.motors;

  const brands = useMemo(
    () => [...new Set((motors || []).map((m) => m.brand))].sort((a, b) => a.localeCompare(b)),
    [motors]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (motors || []).filter((m) => {
      if (brand !== "all" && m.brand !== brand) return false;
      // The page exists for the measured entries — a handful out of hundreds —
      // and without this the only way to reach them is to scroll the catalog.
      if (tested === "tested" && !m.comboSlug) return false;
      if (tested === "specs" && m.comboSlug) return false;
      return !q || `${m.brand} ${m.name}`.toLowerCase().includes(q);
    });
  }, [motors, query, brand, tested]);

  // Reset paging when the filter changes, so a new search does not open on
  // page 5 of the previous result set.
  useEffect(() => setShown(PAGE), [query, brand, tested]);

  // Rank among entries that HAVE test data. Computed over the full catalog, not
  // the filtered view, so which ones are free does not shift as the user
  // searches — otherwise filtering would quietly unlock gated data.
  const testDataRank = useMemo(() => {
    const rank = new Map();
    (motors || []).filter((m) => m.comboSlug).forEach((m, i) => rank.set(m, i));
    return rank;
  }, [motors]);

  const isLocked = (motor) =>
    !isAuthenticated && Boolean(motor.comboSlug) && (testDataRank.get(motor) ?? 0) >= FREE_COMBO_COUNT;

  // Every card opens something. With bench data that is the comparison (gated
  // past the free tier); without, the motor's own spec page — a card that does
  // nothing on click is a dead end, and most of the catalog is spec-only.
  //
  // An open card is a real link, so it survives cmd-click and a crawler. A
  // locked one is not: it runs the sign-in gate first, so it stays a button —
  // and a real <button>, which is keyboard-operable without a key handler.
  // Shared by the cards and the table rows, so the two views can never disagree
  // about where the same motor goes.
  const motorTarget = (motor) =>
    motor.comboSlug ? comboPath(motor.comboSlug) : motorPath(motor.brand, motor.name);

  const cardNav = (motor) =>
    isLocked(motor)
      ? {
          component: "button",
          type: "button",
          onClick: () => requireSignin(() => navigate(comboPath(motor.comboSlug))),
        }
      : {
          component: RouterLink,
          to: motorTarget(motor),
        };

  const visible = filtered.slice(0, shown);

  return (
    <Box sx={{ background: t.ground, color: t.text, minHeight: "100%" }}>
      <Box sx={{ px: { xs: 3, md: 7 }, pt: { xs: 6, md: 9 }, pb: { xs: 4, md: 6 }, textAlign: "center", borderBottom: `1px solid ${t.border}` }}>
        <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: t.accent, fontWeight: 700, mb: 1.5 }}>
          CLAIMED VS MEASURED
        </Box>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: 32, md: 46 }, fontWeight: 700, letterSpacing: "-0.01em", color: t.textStrong }}>
          Motor &amp; propeller test data
        </Box>
        <Box component="p" sx={{ mt: 2, mb: 0, mx: "auto", maxWidth: 660, fontSize: { xs: 15, md: 17 }, color: t.textMuted, lineHeight: 1.6 }}>
          Manufacturers publish a thrust table. We put the same motor and prop on
          our thrust stand and publish what it actually did — thrust, current,
          efficiency, torque and temperature, throttle point by throttle point.
        </Box>

        {data && (
          <Box sx={{ mt: 3.5, display: "flex", justifyContent: "center", gap: { xs: 3, md: 5 }, flexWrap: "wrap" }}>
            {[
              { label: "MOTORS", value: data.count },
              { label: "BRANDS", value: brands.length },
              { label: "BENCH TESTED", value: data.withTestData },
            ].map((stat) => (
              <Box key={stat.label}>
                <Box sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: t.textStrong }}>{stat.value}</Box>
                <Box sx={{ fontSize: 11, letterSpacing: ".1em", color: t.textFaint }}>{stat.label}</Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 3.5, maxWidth: 900, mx: "auto" }}>
          <CollectionToolbar
            query={query}
            onQueryChange={setQuery}
            placeholder="Search a motor…"
            view={view}
            onViewChange={setView}
          >
            {/* A select, not chips: the catalog runs to dozens of brands. */}
            <FilterSelect
              value={brand}
              onChange={setBrand}
              allLabel="All brands"
              options={brands.map((b) => ({ value: b, label: b }))}
            />
            {/* Two options, so chips: this is the page's main question, and
                burying it in a dropdown would hide the reason to be here. */}
            <FilterChips
              value={tested}
              onChange={setTested}
              options={[
                { value: "tested", label: "Bench tested" },
                { value: "specs", label: "Specs only" },
              ]}
            />
          </CollectionToolbar>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 4, md: 5 }, maxWidth: 1200, mx: "auto" }}>
        {!motors ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={26} sx={{ color: t.accent }} />
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: t.textFaint, mb: 2 }}>
              {filtered.length} {filtered.length === 1 ? "motor" : "motors"}
              {tested === "tested" ? " with bench data" : tested === "specs" ? " without bench data" : ""}
              {brand !== "all" ? ` from ${brand}` : ""}
            </Box>

            {view === "table" ? (
              <CollectionTable
                columns={MOTOR_COLUMNS}
                rows={visible}
                rowKey={(m) => `${m.brand}-${m.name}`}
                // Same gate the cards use: a locked motor sends you to sign-in
                // rather than to data the account has not paid for.
                onRowClick={(m) =>
                  isLocked(m)
                    ? requireSignin(() => navigate(comboPath(m.comboSlug)))
                    : navigate(motorTarget(m))
                }
              />
            ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
              {visible.map((m) => {
                const locked = isLocked(m);
                const hasData = Boolean(m.comboSlug);
                return (
                  <Box
                    key={`${m.brand}-${m.name}`}
                    {...cardNav(m)}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                      textAlign: "left",
                      font: "inherit",
                      width: "100%",
                      background: t.surface,
                      border: `1px solid ${hasData ? `${t.accent}55` : t.border}`,
                      transition: "border-color .15s, transform .15s",
                      "&:hover": { borderColor: t.accent, transform: "translateY(-2px)" },
                    }}
                  >
                    <Box sx={{ fontSize: 10.5, letterSpacing: ".1em", color: t.accent, fontWeight: 700, mb: 0.8 }}>
                      {String(m.brand).toUpperCase()}
                    </Box>
                    <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong, mb: 1.5, lineHeight: 1.35, minHeight: 40 }}>
                      {m.name}
                    </Box>

                    <Box sx={{ display: "flex", gap: 2.5, mb: 1.5 }}>
                      {[
                        { label: "KV", value: m.kv ? Math.round(m.kv) : "—" },
                        { label: "WEIGHT", value: m.weightG ? `${Math.round(m.weightG)} g` : "—" },
                        { label: "MAX A", value: m.maxCurrentA ? Math.round(m.maxCurrentA) : "—" },
                      ].map((s) => (
                        <Box key={s.label}>
                          <Box sx={{ fontSize: 10, color: t.textFaint }}>{s.label}</Box>
                          <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong }}>{s.value}</Box>
                        </Box>
                      ))}
                    </Box>

                    {hasData ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: t.accent, fontSize: 13, fontWeight: 600 }}>
                          {locked && <LockOutlinedIcon sx={{ fontSize: 14 }} />}
                          {locked ? "Sign in to view" : "Compare"}
                          {!locked && <ArrowForwardIcon sx={{ fontSize: 15 }} />}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: 11.5, color: t.success }}>
                          <VerifiedOutlinedIcon sx={{ fontSize: 13 }} />
                          Bench tested
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12.5, color: t.textMuted, fontWeight: 600 }}>
                        Specs
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                        <Box component="span" sx={{ fontWeight: 400, color: t.textFaint, ml: 0.5 }}>
                          not bench tested yet
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            )}

            {shown < filtered.length && (
              <Box sx={{ mt: 3.5, textAlign: "center" }}>
                <Button
                  onClick={() => setShown((n) => n + PAGE)}
                  variant="outlined"
                  sx={{ color: t.accent, borderColor: t.accent, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, "&:hover": { borderColor: t.accent, background: `${t.accent}14` } }}
                >
                  Show more ({filtered.length - shown} left)
                </Button>
              </Box>
            )}

            {filtered.length === 0 && (
              <Box sx={{ py: 6, textAlign: "center", color: t.textMuted, fontSize: 14 }}>
                No motor matches that search.
              </Box>
            )}
          </>
        )}

        {/* Manufacturers arrive here to get their motor listed. Submissions go
            to the same moderation queue as community parts — nothing a vendor
            sends lands in the catalog unreviewed. */}
        <Box sx={{ mt: 4, p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface, display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: "wrap" }}>
          <FactoryOutlinedIcon sx={{ color: t.accent, fontSize: 26, mt: 0.3 }} />
          <Box sx={{ flex: "1 1 340px" }}>
            <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 0.8 }}>
              Make motors? Get yours listed.
            </Box>
            <Box sx={{ fontSize: 14, color: t.textMuted, lineHeight: 1.6 }}>
              Send your specs and your published thrust table. We list your figures
              as your claim, with attribution — and when the motor reaches our
              stand, the measured curve is published beside it. No charge, and no
              editing of your numbers.
            </Box>
          </Box>
          <Button
            onClick={() => setSubmitOpen(true)}
            variant="contained"
            sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, "&:hover": { background: t.accentHover } }}
          >
            Submit your motor
          </Button>
        </Box>

        <Box sx={{ mt: 2.5, p: 3, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surfaceAlt, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>Want a combination we haven't tested?</Box>
            <Box sx={{ fontSize: 13.5, color: t.textMuted }}>
              Size it against the model first, or ask us to put it on the stand.
            </Box>
          </Box>
          <Button
            component={RouterLink}
            to={calculatorPath()}
            variant="outlined"
            sx={{ color: t.accent, borderColor: t.accent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { borderColor: t.accent, background: `${t.accent}14` } }}
          >
            Open the calculator
          </Button>
        </Box>
      </Box>

      <SubmitPartDialog open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </Box>
  );
};

export default MotorDataIndex;
